import { Vector2, Vector3, Web3DViewer } from '@technology/web3d';
import { createSequentialEffect } from '@trimble/solidjs-utils';
import { Point2D } from '@trimble/utilities';
import {
  CapabilityCoordinatorBase,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import { DEFAULT_CAMERA_HEIGHT } from '@trimble/web3d-capabilities';
import {
  TileMapCapabilityProvider,
  TileMapProviderProps,
} from '@trimble/web3d-providers';
import { getLogger } from '@trimble/logging';
import {
  Accessor,
  createMemo,
  createRoot,
  createSignal,
  Setter,
} from 'solid-js';
import { ViewMode } from '../../view-mode';
import { buildMapTileBaseUrl, MapTileConfig } from '../map-tile-config';

const MODEL_ID = 'mm-satellite-map';
const BOUNDING_EXTENT = 100000;

export interface TileMapLoadLimitBounds {
  min: Point2D;
  max: Point2D;
}

export type FetchGeoDataFiles = (
  csib64: string
) => Promise<Map<string, ArrayBuffer>>;

export function resolveLoadLimitBounds(
  center: Point2D,
  bounds?: TileMapLoadLimitBounds
): [Vector2, Vector2] {
  if (bounds !== undefined) {
    return [
      new Vector2(bounds.min.x, bounds.min.y),
      new Vector2(bounds.max.x, bounds.max.y),
    ];
  }
  return [
    new Vector2(center.x - BOUNDING_EXTENT, center.y - BOUNDING_EXTENT),
    new Vector2(center.x + BOUNDING_EXTENT, center.y + BOUNDING_EXTENT),
  ];
}

export class TileMapCoordinatesCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  private readonly tileMapProviderPropsSignal: Accessor<
    TileMapProviderProps | undefined
  >;
  private readonly setTileMapProviderProps: Setter<
    TileMapProviderProps | undefined
  >;

  private readonly contentUpdatedSignal: Accessor<void>;
  private readonly triggerContentUpdate: Setter<void>;

  private readonly dispose: () => void;

  constructor(
    protected readonly registryContainer: CapabilityRegistryContainer,
    private readonly viewer: Web3DViewer,
    private readonly fetchGeoDataFiles: FetchGeoDataFiles,
    private readonly mapTileConfig: MapTileConfig,
    /** Csib of the currently loaded dataset, from DataContext. */
    private readonly datasetCsib: Accessor<string | undefined>,
    /** World-coordinate center of the currently loaded dataset, from DataContext. */
    private readonly datasetCenter: Accessor<Point2D | undefined>,
    /** Active pane view mode — basemap is only shown in 2D. */
    private readonly viewMode: Accessor<ViewMode>,
    private readonly datasetBounds: Accessor<
      TileMapLoadLimitBounds | undefined
    > = () => undefined
  ) {
    super(registryContainer);

    const [tileMapProviderProps, setTileMapProviderProps] =
      createSignal<TileMapProviderProps>();
    this.tileMapProviderPropsSignal = tileMapProviderProps;
    this.setTileMapProviderProps = setTileMapProviderProps;

    const [contentUpdated, triggerContentUpdate] = createSignal<void>(
      undefined,
      { equals: false }
    );
    this.contentUpdatedSignal = contentUpdated;
    this.triggerContentUpdate = triggerContentUpdate;

    this.dispose = createRoot((dispose) => {
      const activeGeoReference = createMemo(() => {
        if (this.viewMode().type !== '2D') {
          return undefined;
        }
        const c = this.datasetCsib();
        const ce = this.datasetCenter();
        if (c === undefined || ce === undefined) return undefined;
        return { csib: c, center: ce, bounds: this.datasetBounds() };
      });

      // On every geoReference change while in 2D: unregister the existing
      // TileMapCapabilityProvider, then — if geo is defined — register via
      // setupTileMap. Gating on view mode keeps the coordinator alive across
      // 3D/Image toggles so returning to 2D reliably restores the basemap.
      // eslint-disable-next-line solid/reactivity
      createSequentialEffect(activeGeoReference, async (geo) => {
        await this.registryContainer.unregister(TileMapCapabilityProvider);

        if (geo === undefined) {
          this.triggerContentUpdate();
          return;
        }

        await this.setupTileMap(geo.csib, geo.center, geo.bounds);
      });

      // Position the 2D map camera directly. Using the sync coordinator would
      // leak the geo-reference center into cross-pane sync and move
      // 3D panes on every point-cloud load.
      createSequentialEffect(
        () =>
          this.viewMode().type === '2D'
            ? activeGeoReference()?.center
            : undefined,
        async (center) => {
          if (center === undefined) {
            return;
          }
          this.viewer.camera.position = new Vector3(
            center.x,
            center.y,
            DEFAULT_CAMERA_HEIGHT
          );
        }
      );

      return dispose;
    });
  }

  /** Registers a fresh TileMapCapabilityProvider for the given csib/center. */
  private async setupTileMap(
    csib: string,
    center: Point2D,
    bounds?: TileMapLoadLimitBounds
  ): Promise<void> {
    let geoDataFiles: Map<string, ArrayBuffer> = new Map();
    try {
      geoDataFiles = await this.fetchGeoDataFiles(csib);
    } catch (error) {
      getLogger().error('Failed to fetch geodata files for CSIB', error);
    }

    const loadLimitBounds = resolveLoadLimitBounds(center, bounds);

    const props: TileMapProviderProps = {
      projection: {
        csib,
        geoDataFiles,
      },
      loadLimitBounds,
    };
    this.setTileMapProviderProps(props);

    const tileMapProvider = this.registryContainer.register(
      TileMapCapabilityProvider,
      new TileMapCapabilityProvider(
        this.viewer,
        this.tileMapProviderPropsSignal
      )
    );

    await tileMapProvider.addMapModel({
      baseUrl: buildMapTileBaseUrl(this.mapTileConfig),
      modelId: MODEL_ID,
      projection: props.projection,
      initialPosition: [center.x, center.y],
    });
    tileMapProvider.setSelectedItemId(MODEL_ID);
    this.triggerContentUpdate();
  }

  public get contentUpdated(): Accessor<void> {
    return this.contentUpdatedSignal;
  }

  public async cleanup(): Promise<void> {
    this.dispose();
    await this.registryContainer.unregister(TileMapCapabilityProvider);
  }
}
